/**
 * TTL'd localStorage wrapper. Entries past their expiration are removed on
 * read so a stale value can't outlive its cookie counterpart.
 */

const STORAGE_PREFIX = '_ues_';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type Envelope = { value: string; expiration: number };

function buildKey(name: string): string {
  return `${STORAGE_PREFIX}${name}`;
}

function getLS(): Storage | undefined {
  if (typeof window === 'undefined' || !window.localStorage) {
    return undefined;
  }
  return window.localStorage;
}

function isEnvelope(x: unknown): x is Envelope {
  return (
    !!x &&
    typeof x === 'object' &&
    'value' in x &&
    typeof x.value === 'string' &&
    'expiration' in x &&
    typeof x.expiration === 'number'
  );
}

export function getStorageItem(name: string): string | undefined {
  const ls = getLS();
  if (!ls) {
    return undefined;
  }
  try {
    const raw = ls.getItem(buildKey(name));
    if (raw === null) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isEnvelope(parsed) || parsed.expiration <= Date.now()) {
      removeStorageItem(name);
      return undefined;
    }
    return parsed.value;
  } catch {
    removeStorageItem(name);
    return undefined;
  }
}

export function setStorageItem(name: string, value: string, maxAgeDays: number): void {
  const ls = getLS();
  if (!ls) {
    return;
  }
  try {
    const envelope: Envelope = { value, expiration: Date.now() + maxAgeDays * MS_PER_DAY };
    ls.setItem(buildKey(name), JSON.stringify(envelope));
  } catch {}
}

export function removeStorageItem(name: string): void {
  const ls = getLS();
  if (!ls) {
    return;
  }
  try {
    ls.removeItem(buildKey(name));
  } catch {}
}